import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconBriefcase,
  IconMail,
  IconPhone,
  IconBuilding,
  IconId,
  IconNotes,
} from '@tabler/icons-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@core-ui/Components/ui/dialog';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar';
import type { ContactInfo, LoadingState } from '../types/dashboard.types';
import { Flame } from 'lucide-react';

interface ContactInfoDialogProps {
  contact: ContactInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loadingState: LoadingState;
}

function getInitials (name: string): string {
  if (name === '' || name === null || name === undefined) {
    return '??';
  }
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}

function InfoRow ({ icon, label, value, notAvailable }: InfoRowProps & { notAvailable: string }): React.JSX.Element | null {
  const displayValue = value != null && value !== '' ? value : notAvailable;

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="text-muted-foreground mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{displayValue}</p>
      </div>
    </div>
  );
}

interface NotesRowProps {
  label: string;
  value: string | null | undefined;
}

function NotesRow ({ label, value }: NotesRowProps): React.JSX.Element | null {
  if (value == null || value === '') {
    return null;
  }

  return (
    <div className="py-2">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm text-foreground bg-muted/50 rounded-md p-2 max-h-24 overflow-y-auto">
        {value}
      </p>
    </div>
  );
}

function LoadingView ({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mb-4" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function ErrorState ({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

export default function ContactInfoDialog ({
  contact,
  open,
  onOpenChange,
  loadingState,
}: ContactInfoDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const isLoading = loadingState === 'LOADING';
  const isError = loadingState === 'ERROR';

  const hasNotes =
    (contact?.notes != null && contact.notes !== '') ||
    (contact?.notesSDR != null && contact.notesSDR !== '') ||
    (contact?.notesAgent != null && contact.notesAgent !== '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] border-muted-foreground/30 [&>button]:text-foreground">
        <DialogHeader>
          <DialogTitle data-qa-id="contact-info-title" className="text-foreground">{t('dashboard.contactDialog.title')}</DialogTitle>
        </DialogHeader>

        {isLoading && <LoadingView message={t('dashboard.contactDialog.loading')} />}

        {isError && <ErrorState message={t('dashboard.contactDialog.error')} />}

        {!isLoading && !isError && contact != null && (
          <>
            {/* Contact Header */}
            <div className="flex items-center gap-4 py-4 border-b border-border">
              <Avatar className="h-16 w-16 shadow-lg">
                <AvatarFallback className="bg-primary/20 text-primary text-xl font-semibold">
                  {getInitials(contact.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 data-qa-id="contact-name" className="text-xl font-semibold text-foreground">
                  {contact.name !== '' ? contact.name : t('dashboard.contactDialog.unknownContact')}
                </h3>
                {contact.jobTitle != null && contact.jobTitle !== '' && (
                  <p className="text-sm text-muted-foreground">{contact.jobTitle}</p>
                )}
              </div>
              {(contact.flagged === true) && (
                <Flame className="size-4 md:size-5 shrink-0 text-amber-500/40 fill-current" />
              )}
            </div>

            {/* Contact Details */}
            <div className="space-y-1">
              <InfoRow
                icon={<IconBuilding className="h-4 w-4" />}
                label={t('dashboard.contactDialog.company')}
                value={contact.accountName}
                notAvailable={t('dashboard.contactDialog.notAvailable')}
              />
              <InfoRow
                icon={<IconBriefcase className="h-4 w-4" />}
                label={t('dashboard.contactDialog.jobTitle')}
                value={contact.jobTitle}
                notAvailable={t('dashboard.contactDialog.notAvailable')}
              />
              <InfoRow
                icon={<IconPhone className="h-4 w-4" />}
                label={t('dashboard.contactDialog.phone')}
                value={contact.phone}
                notAvailable={t('dashboard.contactDialog.notAvailable')}
              />
              <InfoRow
                icon={<IconMail className="h-4 w-4" />}
                label={t('dashboard.contactDialog.email')}
                value={contact.email}
                notAvailable={t('dashboard.contactDialog.notAvailable')}
              />
              {contact.crmId != null && contact.crmId !== '' && (
                <InfoRow
                  icon={<IconId className="h-4 w-4" />}
                  label={t('dashboard.contactDialog.crmId')}
                  value={contact.crmId}
                  notAvailable={t('dashboard.contactDialog.notAvailable')}
                />
              )}
            </div>

            {/* Notes Section */}
            {hasNotes && (
              <div className="border-t border-border pt-4 mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <IconNotes className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium text-foreground">{t('dashboard.contactDialog.notes')}</h4>
                </div>
                <div className="space-y-2">
                  <NotesRow label={t('dashboard.contactDialog.generalNotes')} value={contact.notes} />
                  <NotesRow label={t('dashboard.contactDialog.sdrNotes')} value={contact.notesSDR} />
                  <NotesRow label={t('dashboard.contactDialog.agentNotes')} value={contact.notesAgent} />
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
